import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { productService } from '../../services/api'
import { 
  Product, 
  ProductCategory, 
  ProductFilters,
  PaginationParams,
  SearchSuggestion
} from '../../types/api'
import { isApiError } from '../../utils/api'

interface ProductState {
  products: Product[]
  categories: ProductCategory[]
  currentProduct: Product | null
  currentCategory: ProductCategory | null
  pagination: {
    page: number
    per_page: number
    total: number
    last_page: number
  }
  filters: ProductFilters
  loading: boolean
  categoriesLoading: boolean
  creating: boolean
  updating: boolean
  deleting: boolean
  error: string | null
  searchResults: Product[]
  searchSuggestions: SearchSuggestion[]
  searchLoading: boolean
  recentProducts: Product[]
  recentlyUpdated: Product[]
  lowStockProducts: Product[]
  outOfStockProducts: Product[]
  topSellingProducts: Array<{
    product: Product
    quantity_sold: number
    revenue: number
    sales_count: number
  }>
  productStatistics: {
    total_sales: number
    total_quantity_sold: number
    revenue: number
    average_selling_price: number
    profit_margin: number
    sales_trend: Array<{ date: string, quantity: number, amount: number }>
  } | null
  priceHistory: Array<{
    id: string
    price: number
    previous_price: number
    effective_date: string
    created_at: string
  }>
  stockHistory: Array<{
    id: string
    type: string
    quantity: number
    previous_quantity: number
    new_quantity: number
    reason?: string
    created_at: string
  }>
  bulkOperationStatus: {
    operation: 'update' | 'delete' | 'export' | 'import' | 'price_update' | null
    progress: number
    completed: number
    failed: number
    total: number
  }
}

const initialState: ProductState = {
  products: [],
  categories: [],
  currentProduct: null,
  currentCategory: null,
  pagination: {
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  },
  filters: {},
  loading: false,
  categoriesLoading: false,
  creating: false,
  updating: false,
  deleting: false,
  error: null,
  searchResults: [],
  searchSuggestions: [],
  searchLoading: false,
  recentProducts: [],
  recentlyUpdated: [],
  lowStockProducts: [],
  outOfStockProducts: [],
  topSellingProducts: [],
  productStatistics: null,
  priceHistory: [],
  stockHistory: [],
  bulkOperationStatus: {
    operation: null,
    progress: 0,
    completed: 0,
    failed: 0,
    total: 0
  }
}

// Async thunks for products
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (params: { filters?: ProductFilters; pagination?: PaginationParams }, { rejectWithValue }) => {
    try {
      const response = await productService.getProducts(params.filters, params.pagination)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch products')
    }
  }
)

export const fetchProduct = createAsyncThunk(
  'products/fetchProduct',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await productService.getProduct(id)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch product')
    }
  }
)

export const createProduct = createAsyncThunk(
  'products/createProduct',
  async (data: Omit<Product, 'id' | 'created_at' | 'updated_at'>, { rejectWithValue }) => {
    try {
      const response = await productService.createProduct(data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to create product')
    }
  }
)

export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async ({ id, data }: { id: string; data: Partial<Product> }, { rejectWithValue }) => {
    try {
      const response = await productService.updateProduct(id, data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to update product')
    }
  }
)

export const deleteProduct = createAsyncThunk(
  'products/deleteProduct',
  async (id: string, { rejectWithValue }) => {
    try {
      await productService.deleteProduct(id)
      return id
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to delete product')
    }
  }
)

export const searchProducts = createAsyncThunk(
  'products/searchProducts',
  async (query: string, { rejectWithValue }) => {
    try {
      const response = await productService.searchProducts(query)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to search products')
    }
  }
)

export const getProductSuggestions = createAsyncThunk(
  'products/getSuggestions',
  async (query: string, { rejectWithValue }) => {
    try {
      const response = await productService.getProductSuggestions(query)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to get product suggestions')
    }
  }
)

export const fetchProductByBarcode = createAsyncThunk(
  'products/fetchByBarcode',
  async (barcode: string, { rejectWithValue }) => {
    try {
      const response = await productService.getProductByBarcode(barcode)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch product by barcode')
    }
  }
)

export const generateBarcode = createAsyncThunk(
  'products/generateBarcode',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await productService.generateBarcode(id)
      return { id, barcode: response.data.barcode }
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to generate barcode')
    }
  }
)

// Category thunks
export const fetchCategories = createAsyncThunk(
  'products/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await productService.getCategories()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch categories')
    }
  }
)

export const createCategory = createAsyncThunk(
  'products/createCategory',
  async (data: Omit<ProductCategory, 'id' | 'created_at' | 'updated_at'>, { rejectWithValue }) => {
    try {
      const response = await productService.createCategory(data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to create category')
    }
  }
)

export const updateCategory = createAsyncThunk(
  'products/updateCategory',
  async ({ id, data }: { id: string; data: Partial<ProductCategory> }, { rejectWithValue }) => {
    try {
      const response = await productService.updateCategory(id, data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to update category')
    }
  }
)

export const deleteCategory = createAsyncThunk(
  'products/deleteCategory',
  async (id: string, { rejectWithValue }) => {
    try {
      await productService.deleteCategory(id)
      return id
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to delete category')
    }
  }
)

// Inventory thunks
export const updateStock = createAsyncThunk(
  'products/updateStock',
  async ({ id, quantity, type }: { id: string; quantity: number; type: 'add' | 'subtract' | 'set' }, { rejectWithValue }) => {
    try {
      const response = await productService.updateStock(id, quantity, type)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to update stock')
    }
  }
)

export const fetchStockHistory = createAsyncThunk(
  'products/fetchStockHistory',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await productService.getStockHistory(id)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch stock history')
    }
  }
)

export const fetchLowStockProducts = createAsyncThunk(
  'products/fetchLowStock',
  async (threshold: number = 10, { rejectWithValue }) => {
    try {
      const response = await productService.getLowStockProducts(threshold)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch low stock products')
    }
  }
)

export const fetchOutOfStockProducts = createAsyncThunk(
  'products/fetchOutOfStock',
  async (_, { rejectWithValue }) => {
    try {
      const response = await productService.getOutOfStockProducts()
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch out of stock products')
    }
  }
)

// Pricing thunks
export const updatePrice = createAsyncThunk(
  'products/updatePrice',
  async ({ id, price }: { id: string; price: number }, { rejectWithValue }) => {
    try {
      const response = await productService.updatePrice(id, price)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to update price')
    }
  }
)

export const fetchPriceHistory = createAsyncThunk(
  'products/fetchPriceHistory',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await productService.getPriceHistory(id)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch price history')
    }
  }
)

// Analytics thunks
export const fetchProductStatistics = createAsyncThunk(
  'products/fetchStatistics',
  async ({ id, period }: { id: string; period?: string }, { rejectWithValue }) => {
    try {
      const response = await productService.getProductStatistics(id, period)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch product statistics')
    }
  }
)

export const fetchTopSellingProducts = createAsyncThunk(
  'products/fetchTopSelling',
  async ({ limit = 10, period }: { limit?: number; period?: string }, { rejectWithValue }) => {
    try {
      const response = await productService.getTopSellingProducts(limit, period)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch top selling products')
    }
  }
)

export const fetchRecentProducts = createAsyncThunk(
  'products/fetchRecent',
  async (limit: number = 10, { rejectWithValue }) => {
    try {
      const response = await productService.getRecentProducts(limit)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch recent products')
    }
  }
)

export const fetchRecentlyUpdated = createAsyncThunk(
  'products/fetchRecentlyUpdated',
  async (limit: number = 10, { rejectWithValue }) => {
    try {
      const response = await productService.getRecentlyUpdated(limit)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch recently updated products')
    }
  }
)

// Bulk operations
export const bulkUpdateProducts = createAsyncThunk(
  'products/bulkUpdate',
  async ({ ids, data }: { ids: string[]; data: Partial<Product> }, { rejectWithValue }) => {
    try {
      const response = await productService.bulkUpdate(ids, data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to bulk update products')
    }
  }
)

export const bulkDeleteProducts = createAsyncThunk(
  'products/bulkDelete',
  async (ids: string[], { rejectWithValue }) => {
    try {
      const response = await productService.bulkDelete(ids)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to bulk delete products')
    }
  }
)

export const bulkUpdatePrices = createAsyncThunk(
  'products/bulkUpdatePrices',
  async (updates: Array<{ id: string, price: number }>, { rejectWithValue }) => {
    try {
      const response = await productService.bulkUpdatePrices(updates)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to bulk update prices')
    }
  }
)

export const importProducts = createAsyncThunk(
  'products/import',
  async (file: File, { rejectWithValue }) => {
    try {
      const response = await productService.importProducts(file)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to import products')
    }
  }
)

// Create slice
const productSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setFilters: (state, action: PayloadAction<ProductFilters>) => {
      state.filters = action.payload
    },
    updateFilters: (state, action: PayloadAction<Partial<ProductFilters>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {}
    },
    setPagination: (state, action: PayloadAction<Partial<typeof initialState.pagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    setCurrentProduct: (state, action: PayloadAction<Product | null>) => {
      state.currentProduct = action.payload
    },
    setCurrentCategory: (state, action: PayloadAction<ProductCategory | null>) => {
      state.currentCategory = action.payload
    },
    clearSearchResults: (state) => {
      state.searchResults = []
      state.searchSuggestions = []
    },
    clearProductStatistics: (state) => {
      state.productStatistics = null
    },
    clearPriceHistory: (state) => {
      state.priceHistory = []
    },
    clearStockHistory: (state) => {
      state.stockHistory = []
    },
    resetBulkOperationStatus: (state) => {
      state.bulkOperationStatus = {
        operation: null,
        progress: 0,
        completed: 0,
        failed: 0,
        total: 0
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch Products
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false
        state.products = action.payload.data
        state.pagination = {
          page: action.payload.page,
          per_page: action.payload.per_page,
          total: action.payload.total,
          last_page: action.payload.last_page
        }
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch Single Product
    builder
      .addCase(fetchProduct.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProduct.fulfilled, (state, action) => {
        state.loading = false
        state.currentProduct = action.payload
      })
      .addCase(fetchProduct.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Create Product
    builder
      .addCase(createProduct.pending, (state) => {
        state.creating = true
        state.error = null
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.creating = false
        state.products.unshift(action.payload)
        state.currentProduct = action.payload
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.creating = false
        state.error = action.payload as string
      })

    // Update Product
    builder
      .addCase(updateProduct.pending, (state) => {
        state.updating = true
        state.error = null
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.updating = false
        const index = state.products.findIndex(product => product.id === action.payload.id)
        if (index !== -1) {
          state.products[index] = action.payload
        }
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct = action.payload
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.updating = false
        state.error = action.payload as string
      })

    // Delete Product
    builder
      .addCase(deleteProduct.pending, (state) => {
        state.deleting = true
        state.error = null
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.deleting = false
        state.products = state.products.filter(product => product.id !== action.payload)
        if (state.currentProduct?.id === action.payload) {
          state.currentProduct = null
        }
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.deleting = false
        state.error = action.payload as string
      })

    // Search Products
    builder
      .addCase(searchProducts.pending, (state) => {
        state.searchLoading = true
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.searchLoading = false
        state.searchResults = action.payload
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.searchLoading = false
        state.error = action.payload as string
      })

    // Get Product Suggestions
    builder
      .addCase(getProductSuggestions.fulfilled, (state, action) => {
        state.searchSuggestions = action.payload
      })

    // Fetch Product by Barcode
    builder
      .addCase(fetchProductByBarcode.fulfilled, (state, action) => {
        if (action.payload) {
          state.currentProduct = action.payload
        }
      })

    // Generate Barcode
    builder
      .addCase(generateBarcode.fulfilled, (state, action) => {
        const index = state.products.findIndex(product => product.id === action.payload.id)
        if (index !== -1) {
          state.products[index].barcode = action.payload.barcode
        }
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct.barcode = action.payload.barcode
        }
      })

    // Categories
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.categoriesLoading = true
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false
        state.categories = action.payload
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.categoriesLoading = false
        state.error = action.payload as string
      })

    builder
      .addCase(createCategory.fulfilled, (state, action) => {
        state.categories.push(action.payload)
        state.currentCategory = action.payload
      })

    builder
      .addCase(updateCategory.fulfilled, (state, action) => {
        const index = state.categories.findIndex(cat => cat.id === action.payload.id)
        if (index !== -1) {
          state.categories[index] = action.payload
        }
        if (state.currentCategory?.id === action.payload.id) {
          state.currentCategory = action.payload
        }
      })

    builder
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categories = state.categories.filter(cat => cat.id !== action.payload)
        if (state.currentCategory?.id === action.payload) {
          state.currentCategory = null
        }
      })

    // Inventory
    builder
      .addCase(updateStock.fulfilled, (state, action) => {
        const index = state.products.findIndex(product => product.id === action.payload.id)
        if (index !== -1) {
          state.products[index] = action.payload
        }
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct = action.payload
        }
      })

    builder
      .addCase(fetchStockHistory.fulfilled, (state, action) => {
        state.stockHistory = action.payload
      })

    builder
      .addCase(fetchLowStockProducts.fulfilled, (state, action) => {
        state.lowStockProducts = action.payload
      })

    builder
      .addCase(fetchOutOfStockProducts.fulfilled, (state, action) => {
        state.outOfStockProducts = action.payload
      })

    // Pricing
    builder
      .addCase(updatePrice.fulfilled, (state, action) => {
        const index = state.products.findIndex(product => product.id === action.payload.id)
        if (index !== -1) {
          state.products[index] = action.payload
        }
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct = action.payload
        }
      })

    builder
      .addCase(fetchPriceHistory.fulfilled, (state, action) => {
        state.priceHistory = action.payload
      })

    // Analytics
    builder
      .addCase(fetchProductStatistics.fulfilled, (state, action) => {
        state.productStatistics = action.payload
      })

    builder
      .addCase(fetchTopSellingProducts.fulfilled, (state, action) => {
        state.topSellingProducts = action.payload
      })

    builder
      .addCase(fetchRecentProducts.fulfilled, (state, action) => {
        state.recentProducts = action.payload
      })

    builder
      .addCase(fetchRecentlyUpdated.fulfilled, (state, action) => {
        state.recentlyUpdated = action.payload
      })

    // Bulk Operations
    builder
      .addCase(bulkUpdateProducts.pending, (state) => {
        state.bulkOperationStatus.operation = 'update'
      })
      .addCase(bulkUpdateProducts.fulfilled, (state, action) => {
        state.bulkOperationStatus = {
          operation: null,
          progress: 100,
          completed: action.payload.updated,
          failed: action.payload.failed.length,
          total: action.payload.updated + action.payload.failed.length
        }
      })

    builder
      .addCase(bulkDeleteProducts.pending, (state) => {
        state.bulkOperationStatus.operation = 'delete'
      })
      .addCase(bulkDeleteProducts.fulfilled, (state, action) => {
        state.bulkOperationStatus = {
          operation: null,
          progress: 100,
          completed: action.payload.deleted,
          failed: action.payload.failed.length,
          total: action.payload.deleted + action.payload.failed.length
        }
      })

    builder
      .addCase(bulkUpdatePrices.pending, (state) => {
        state.bulkOperationStatus.operation = 'price_update'
      })
      .addCase(bulkUpdatePrices.fulfilled, (state, action) => {
        state.bulkOperationStatus = {
          operation: null,
          progress: 100,
          completed: action.payload.updated,
          failed: action.payload.failed.length,
          total: action.payload.updated + action.payload.failed.length
        }
      })

    builder
      .addCase(importProducts.pending, (state) => {
        state.bulkOperationStatus.operation = 'import'
      })
      .addCase(importProducts.fulfilled, (state, action) => {
        state.bulkOperationStatus = {
          operation: null,
          progress: 100,
          completed: action.payload.imported + action.payload.updated,
          failed: action.payload.failed,
          total: action.payload.imported + action.payload.updated + action.payload.failed
        }
      })
  },
})

// Export actions
export const {
  clearError,
  setFilters,
  updateFilters,
  clearFilters,
  setPagination,
  setCurrentProduct,
  setCurrentCategory,
  clearSearchResults,
  clearProductStatistics,
  clearPriceHistory,
  clearStockHistory,
  resetBulkOperationStatus
} = productSlice.actions

// Selectors
export const selectProducts = (state: { products: ProductState }) => state.products.products
export const selectCategories = (state: { products: ProductState }) => state.products.categories
export const selectCurrentProduct = (state: { products: ProductState }) => state.products.currentProduct
export const selectCurrentCategory = (state: { products: ProductState }) => state.products.currentCategory
export const selectProductPagination = (state: { products: ProductState }) => state.products.pagination
export const selectProductFilters = (state: { products: ProductState }) => state.products.filters
export const selectProductLoading = (state: { products: ProductState }) => state.products.loading
export const selectProductError = (state: { products: ProductState }) => state.products.error
export const selectProductSearchResults = (state: { products: ProductState }) => state.products.searchResults
export const selectProductSuggestions = (state: { products: ProductState }) => state.products.searchSuggestions
export const selectRecentProducts = (state: { products: ProductState }) => state.products.recentProducts
export const selectLowStockProducts = (state: { products: ProductState }) => state.products.lowStockProducts
export const selectOutOfStockProducts = (state: { products: ProductState }) => state.products.outOfStockProducts
export const selectTopSellingProducts = (state: { products: ProductState }) => state.products.topSellingProducts
export const selectProductStatistics = (state: { products: ProductState }) => state.products.productStatistics
export const selectPriceHistory = (state: { products: ProductState }) => state.products.priceHistory
export const selectStockHistory = (state: { products: ProductState }) => state.products.stockHistory
export const selectProductBulkOperationStatus = (state: { products: ProductState }) => state.products.bulkOperationStatus

export default productSlice.reducer 